import { FC, useMemo, useRef, useState } from "react";
import { mockPriceHistory, currentPrice, priceChange24h, priceUsd } from "@/lib/mockData";
import {
  buildPriceChartViewModel,
  DESKTOP_TIME_LABELS,
  PriceChartData,
} from "./priceChartModel";
import type { Market } from "@module-bindings/types";
import { formatMarketPairLabel } from "@/lib/marketUtils";
import { PairIcon } from "@/components/TokenIcon";

interface PriceChartProps {
  compact?: boolean;
  selectedMarket: Market | null;
  markets: readonly Market[];
  onSelectMarket: (market: Market) => void;
}

export const PriceChart: FC<PriceChartProps> = ({ compact = false, selectedMarket, markets, onSelectMarket }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const pairLabel = selectedMarket ? formatMarketPairLabel(selectedMarket) : "No Markets";

  const chartData = useMemo<PriceChartData>(
    () => ({
      currentPrice,
      pairLabel,
      priceChange24h,
      priceUsd,
      history: mockPriceHistory,
    }),
    [pairLabel],
  );

  const {
    areaPath,
    gridLines,
    isPositive,
    padding,
    path,
    strokeColor,
    svgHeight,
    svgWidth,
  } = useMemo(() => buildPriceChartViewModel(chartData, compact), [chartData, compact]);

  return (
    <div
      className={`flex flex-col overflow-hidden rounded-xl bg-[#23141A] ${
        compact ? "" : "min-h-0 flex-1"
      }`}
    >
      {/* Token pair header */}
      <div className={`flex items-center justify-between ${compact ? "px-4 py-3" : "px-5 pt-5 pb-3"}`}>
        {/* Left: icon + price + pair name */}
        <div className="flex items-center gap-3">
          {selectedMarket ? (
            <PairIcon
              base={selectedMarket.tradingPair.base}
              quote={selectedMarket.tradingPair.quote}
              size={40}
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-sr-espresso border-2 border-sr-pink-muted flex items-center justify-center shrink-0">
              <span className="text-xs font-bold text-sr-neutral-500">?</span>
            </div>
          )}
          <div className="flex flex-col">
            <div className="flex items-center gap-2.5">
              <span className="text-xl font-medium text-white font-display tabular-nums">
                {chartData.currentPrice.toFixed(5)}
              </span>
              <span className="text-sm text-sr-neutral-300">
                {pairLabel}
              </span>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-sr-neutral-500">
                <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.2" />
                <path d="M7 6V10M7 4.5V5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
              </svg>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-sr-neutral-300">${chartData.priceUsd}</span>
              <span
                className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                  isPositive
                    ? "text-sr-mint bg-sr-mint/12"
                  : "text-sr-salmon bg-sr-salmon/12"
                }`}
              >
                {isPositive ? "+" : ""}{chartData.priceChange24h}%
              </span>
            </div>
          </div>
          {/* Market dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen((v) => !v)}
              onBlur={(e) => {
                if (!dropdownRef.current?.contains(e.relatedTarget as Node)) {
                  setDropdownOpen(false);
                }
              }}
              className="ml-1 text-sr-neutral-300 hover:text-white transition-colors"
              aria-label="Select market"
              disabled={markets.length === 0}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {dropdownOpen && markets.length > 0 && (
              <div className="absolute left-0 top-full z-50 mt-2 min-w-[180px] rounded-xl border border-sr-border-subtle bg-[#23141A] p-1 shadow-xl">
                {markets.map((market) => {
                  const label = formatMarketPairLabel(market);
                  const isActive = selectedMarket?.key === market.key;
                  return (
                    <button
                      key={market.key}
                      onClick={() => {
                        onSelectMarket(market);
                        setDropdownOpen(false);
                      }}
                      className={`flex w-full items-center rounded-lg px-3 py-2 text-sm transition-colors ${
                        isActive
                          ? "bg-sr-lemon-muted/10 text-sr-lemon-muted"
                          : "text-sr-neutral-300 hover:bg-sr-neutral-900 hover:text-white"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right: timeframe + chart type controls (desktop only) */}
        {!compact && (
          <div className="flex items-center gap-1.5">
            <button className="px-3 py-1.5 text-xs rounded-lg bg-sr-neutral-900 text-sr-lemon-muted font-medium border border-sr-border-subtle">
              1H
            </button>
            <button className="p-2 text-sr-neutral-500 hover:text-sr-neutral-300 rounded-lg hover:bg-sr-neutral-900 transition-colors">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M4 13L7.5 6L10 10L14 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button className="p-2 text-sr-neutral-500 hover:text-sr-neutral-300 rounded-lg hover:bg-sr-neutral-900 transition-colors">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <rect x="3" y="5" width="3" height="8" rx="0.5" stroke="currentColor" strokeWidth="1.2" />
                <rect x="7.5" y="3" width="3" height="12" rx="0.5" stroke="currentColor" strokeWidth="1.2" />
                <rect x="12" y="7" width="3" height="6" rx="0.5" stroke="currentColor" strokeWidth="1.2" />
              </svg>
            </button>
            <button className="p-2 text-sr-neutral-500 hover:text-sr-neutral-300 rounded-lg hover:bg-sr-neutral-900 transition-colors">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M3 3V15H15" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                <path d="M6 12L9 8L12 10L15 5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Chart */}
      <div className={compact ? "px-2 pb-2" : "relative min-h-0 flex-1 px-1 pb-1"}>
        <svg
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          className={compact ? "w-full" : "h-full w-full"}
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="chartAreaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={strokeColor} stopOpacity="0.08" />
              <stop offset="100%" stopColor={strokeColor} stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Grid lines */}
          {gridLines.map((gl, i) => (
            <g key={i}>
              <line
                x1={padding.left}
                y1={gl.y}
                x2={svgWidth - padding.right}
                y2={gl.y}
                stroke="var(--color-sr-border-subtle)"
                strokeWidth="0.5"
                strokeDasharray="4 4"
                opacity="0.3"
              />
            </g>
          ))}

          {/* Area fill */}
          <path d={areaPath} fill="url(#chartAreaGrad)" />

          {/* Line */}
          <path
            d={path}
            fill="none"
            stroke={strokeColor}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {!compact && (
          <div className="pointer-events-none absolute inset-y-0 right-3">
            {gridLines.map((gl, i) => (
              <span
                key={i}
                className="absolute right-0 -translate-y-1/2 text-[11px] text-sr-neutral-500"
                style={{
                  top: `${(gl.y / svgHeight) * 100}%`,
                  fontFamily: "var(--font-display)",
                }}
              >
                {gl.label}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* X-axis time labels (desktop only) */}
      {!compact && (
        <div className="flex justify-between px-4 pb-3 text-[10px] text-sr-neutral-500 tabular-nums">
          {DESKTOP_TIME_LABELS.map((t, i) => (
            <span key={i}>{t}</span>
          ))}
        </div>
      )}
    </div>
  );
};
